import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import PropTypes from "prop-types";
import {
  Box,
  Grid,
  Typography,
  TextField,
  Autocomplete,
  Select,
  MenuItem,
  IconButton,
  Popper,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  InputAdornment,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import { useTranslation } from "react-i18next";
import BackdropComponent from "components/Spinner/Spinner";
import SnackBar from "components/SnackBar/SnackBar";
import pillThemeConfig from "styles/pillThemeConfig";
import {
  CustomTooltip,
  useStyles,
} from "components/RosterConfig/RosterConfigStyle";
import {
  getParentClientWithClassRequestDetails,
  searchClassRequestDetailsByParentClient,
  techEquipSolution,
  getdistributionType,
  getRosterConfigByRosterId,
  searchEmployeeProfile,
  saveRosterManagementFields,
} from "services/userServices";
import applicationUsageLog from "utils/applicationUsagesLog";
import Alert from "@mui/material/Alert";

const SubHeading = styled(Typography)(({ theme }) => ({
  fontFamily: "Urbanist, Arial, sans-serif",
  fontSize: "12px",
  letterSpacing: "0.10em",
  fontWeight: "bold",
  paddingBottom: theme.spacing(0.75),
}));

const CustomPopper = (props) => <Popper {...props} placement="bottom-start" />;

// Maps picker "type" (og | fdo | trainer) to the row's field
const PERSON_FIELD_MAP = {
  og: { nameField: "ogName", emailField: "ogEmail", idField: "ogOracleId" },
  fdo: { nameField: "fdoName", emailField: "fdoEmail", idField: "fdoOracleId" },
  trainer: {
    nameField: "trainerName",
    emailField: "trainerEmail",
    idField: "trainerOracleId",
  },
};

//searchEmployeeProfile: a 7-digit Oracle Id, a name of 3+ letters or formed email.
const isValidPersonSearch = (input) => {
  const trimmed = input.trim();
  if (/^\d+$/.test(trimmed)) return trimmed.length === 7;
  if (/^[a-zA-Z\s]+$/.test(trimmed)) return trimmed.length >= 3;
  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) return true;
  return false;
};

const ellipsisSx = {
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const createData = (row) => ({
  ClassRequestId: row.classRequestId,
  ClassRosterId: row.classRosterId,
  DistributionTypeId: row.distributionTypeId,
  OgOracleId: row.ogOracleId,
  FdoOracleId: row.fdoOracleId,
  TrainerOracleId: row.trainerOracleId,
  RequestedBy: localStorage.getItem("User"),
});

// One OG / FDO / Trainer cell. Double-click to edit
function PersonSearchCell({
  t,
  placeholder,
  nameValue,
  emailValue,
  isOpen,
  searchResults,
  onOpen,
  onClose,
  onInputChange,
  onNameTyped,
  onSelect,
  onSearch,
  isEditing,
  onStartEdit,
  onStopEdit,
}) {
  if (!isEditing) {
    return (
      <Tooltip title={emailValue || ""} placement="top" arrow>
        <Typography
          variant="body2"
          onDoubleClick={onStartEdit}
          sx={{ ...ellipsisSx, cursor: "pointer", minHeight: "24px" }}
        >
          {nameValue || t("select.none", "—")}
        </Typography>
      </Tooltip>
    );
  }

  return (
    <Box
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          onStopEdit();
        }
      }}
      onKeyDownCapture={(event) => {       
        if (event.key === "Enter") {
          event.preventDefault();
          event.stopPropagation();
        }
      }}
    >
      <Autocomplete
        freeSolo
        size="small"
        options={searchResults}
        getOptionLabel={(option) => option.Display || ""}
        onInputChange={onInputChange}
        onChange={(event, value) => {
          onSelect(event, value);
          onStopEdit();
        }}
        inputValue={nameValue || ""}
        PopperComponent={CustomPopper}
        open={isOpen && searchResults.length > 0}
        onOpen={onOpen}
        onClose={onClose}
        renderInput={(params) => (
          <TextField
            {...params}
            autoFocus
            fullWidth
            size="small"
            variant="outlined"
            placeholder={placeholder}
            value={nameValue || ""}
            onChange={(event) => onNameTyped(event.target.value)}
            onKeyDown={(event) => {              
              if (event.key === "Enter") {
                event.preventDefault();
              }
              if (event.key === "Escape") {
                onStopEdit();
              }
            }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <InputAdornment position="end">
                  <CustomTooltip title={t("toolTipSearch", "Search")}>
                    <IconButton
                      size="small"
                      type="button"
                      onClick={() => onSearch(nameValue)}
                    >
                      <SearchIcon fontSize="small" />
                    </IconButton>
                  </CustomTooltip>
                  {params.InputProps.endAdornment}
                </InputAdornment>
              ),
            }}
          />
        )}
      />
    </Box>
  );
}
PersonSearchCell.propTypes = {
  t: PropTypes.func.isRequired,
  placeholder: PropTypes.string.isRequired,
  nameValue: PropTypes.string,
  emailValue: PropTypes.string,
  isOpen: PropTypes.bool.isRequired,
  searchResults: PropTypes.array.isRequired,
  onOpen: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onInputChange: PropTypes.func.isRequired,
  onNameTyped: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  onSearch: PropTypes.func.isRequired,
  isEditing: PropTypes.bool.isRequired,
  onStartEdit: PropTypes.func.isRequired,
  onStopEdit: PropTypes.func.isRequired,
};

//project expand/collapse
function ProjectCell({ t, project, isExpanded, onExpand, onCollapse }) {
  const projects = project ? project.split(",") : [];

  if (projects.length <= 1) {
    return (
      <Typography
        variant="body2"
        sx={{ overflowWrap: "break-word", wordBreak: "break-word" }}
      >
        {project || t("typography.na", "N/A")}
      </Typography>
    );
  }

  return (
    <Tooltip
      title={
        <Typography variant="body2" sx={{ fontSize: "1rem" }}>
          {isExpanded
            ? projects.map((item, idx) => (
                <Grid item key={`${idx}-${item}`}>
                  <Typography variant="subtitle1">{item}</Typography>
                </Grid>
              ))
            : projects[0]}
        </Typography>
      }
      placement="bottom-start"
      arrow
    >
      <Typography
        variant="body2"
        sx={{ overflowWrap: "break-word", wordBreak: "break-word" }}
      >
        {isExpanded ? (
          <>
            {projects.map((item, idx) => (
              <Grid item key={`${idx}-${item}`}>
                <Typography variant="subtitle1">{item}</Typography>
              </Grid>
            ))}
            <Tooltip
              title={
                <Typography variant="body2" sx={{ fontSize: "1rem" }}>
                  {t("typography.click_to_collapse", "Click to collapse")}
                </Typography>
              }
              placement="right"
              arrow
            >
              <IconButton
                sx={{
                  cursor: "pointer",
                  color: "#3299FF",
                  background: "none",
                  border: "none",
                  padding: 0,
                  font: "inherit",
                }}
                onClick={(event) => {
                  onCollapse();
                  event.stopPropagation();
                }}
              >
                {t("iconbutton.all_projects", "Show less")}
              </IconButton>
            </Tooltip>
          </>
        ) : (
          <>
            {projects[0]}{" "}
            <Tooltip
              title={
                <Typography variant="body2" sx={{ fontSize: "1rem" }}>
                  {t("typography.click_to_expand", "Click to expand")}
                </Typography>
              }
              placement="right"
              arrow
            >
              <IconButton
                sx={{
                  cursor: "pointer",
                  color: "#3299FF",
                  background: "none",
                  border: "none",
                  padding: 0,
                  font: "inherit",
                }}
                onClick={(event) => {
                  onExpand();
                  event.stopPropagation();
                }}
              >
                {t("iconbutton._", "+")}
                {projects.length - 1}
              </IconButton>
            </Tooltip>
          </>
        )}
      </Typography>
    </Tooltip>
  );
}
ProjectCell.propTypes = {
  t: PropTypes.func.isRequired,
  project: PropTypes.string,
  isExpanded: PropTypes.bool.isRequired,
  onExpand: PropTypes.func.isRequired,
  onCollapse: PropTypes.func.isRequired,
};

export default function RosterManagement({ setRosterManagementLength }) {
  const { t } = useTranslation(["RosterManagement"], { useSuspense: false });
  const classes = useStyles();  
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "",
  });
  const snackReset = () => setSnack({ open: false, message: "", severity: "" });
  const showError = (message) =>
    setSnack({ open: true, message, severity: "error" });

  // Filters
  const [parentClientDetail, setParentClientDetail] = useState([]);
  const [selectedParentClient, setSelectedParentClient] = useState(null);
  const [rawClassRequests, setRawClassRequests] = useState([]);
  const [selectedWave, setSelectedWave] = useState(null);

  //Dropdown option lists Tech Equipment Solution/Distribution Type
  const [techEquipOptions, setTechEquipOptions] = useState([]);
  const [distributionOptions, setDistributionOptions] = useState([]);

  //Grid rows
  const [rows, setRows] = useState([]);
  const [rowsLoading, setRowsLoading] = useState(false);

  //show all projects
  const [expandedProjectRowId, setExpandedProjectRowId] = useState(null);

  // Double-click-to-edit: which single cell (row + field)
  const [editingCell, setEditingCell] = useState(null); // { classRosterId, field } | null
  const isEditingCell = (classRosterId, field) =>
    editingCell?.classRosterId === classRosterId &&
    editingCell?.field === field;
  const startEdit = (classRosterId, field) =>
    setEditingCell({ classRosterId, field });
  const stopEdit = () => setEditingCell(null);

  // ── OG / FDO / Trainer search 
  const [searchResults, setSearchResults] = useState([]);
  const [activeSearchField, setActiveSearchField] = useState(null); // "og" | "fdo" | "trainer"
  const [activeSearchRowId, setActiveSearchRowId] = useState(null);
  const [isAutocompleteOpen, setIsAutocompleteOpen] = useState(false);


  // INITIAL LOAD
  useEffect(() => {
    const loadParentClients = async () => {
      setLoading(true);
      try {
        applicationUsageLog(
          localStorage.getItem("User"),
          localStorage.getItem("EmailId"),
          "ASTT",
          "Roster Management",
        );
        const response = await getParentClientWithClassRequestDetails();
        if (response?.message === "Success") {
          const filtered = response.data.filter(
            (pc) =>
              (pc.RequestStatus === "In Progress" ||
                pc.RequestStatus === null) &&
              pc.Status === true,
          );
          const map = new Map();
          filtered.forEach((item) => {
            if (!map.has(item.ParentClientCode)) {
              map.set(item.ParentClientCode, {
                ParentClientId: item.ParentClientId,
                ParentClientCode: item.ParentClientCode,
                ParentClientName: item.ParentClientName,
              });
            }
          });
          setParentClientDetail(
            Array.from(map.values()).sort((a, b) =>
              a.ParentClientName.localeCompare(b.ParentClientName),
            ),
          );
        }
      } catch (error) {
        showError(
          t(
            "alert.error_fetching_parent_client_details",
            "Unable to load parent clients.",
          ),
        );
      } finally {
        setLoading(false);
      }
    };

    const loadDropdownOptions = async () => {
      try {
        const [techRes, distRes] = await Promise.all([
          techEquipSolution(),
          getdistributionType(),
        ]);
        if (techRes?.status === true) setTechEquipOptions(techRes.data);
        if (distRes?.status === true) setDistributionOptions(distRes.data);
      } catch (error) {
        showError(
          t(
            "alert.error_fetching_dropdown_options",
            "Unable to load field options.",
          ),
        );
      }
    };

    loadParentClients();
    loadDropdownOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // PARENT CLIENT CHANGE 
  const handleParentClientChange = async (newValue) => {
    setSelectedParentClient(newValue);
    setSelectedWave(null);
    setRows([]);

    if (!newValue) {
      setRawClassRequests([]);
      return;
    }

    setLoading(true);
    try {
      const response = await searchClassRequestDetailsByParentClient({
        parentClientCode: newValue.ParentClientCode,
        requestStatus: "In Progress",
        pageNumber: 1,
        pageSize: 9999,
      });
      if (response?.message === "Success" && response.data) {
        setRawClassRequests(response.data);
      } else {
        setRawClassRequests([]);
      }
    } catch (error) {
      showError(
        t(
          "alert.error_fetching_class_requests",
          "Unable to load class requests for this client.",
        ),
      );
      setRawClassRequests([]);
    } finally {
      setLoading(false);
    }
  };

  // Distinct wave numbers for the selected Parent Client
  const waveOptions = useMemo(() => {
    const waves = new Set();
    rawClassRequests.forEach((cr) => {
      if (cr.wave) waves.add(cr.wave);
    });
    return Array.from(waves).sort((a, b) => Number(a) - Number(b));
  }, [rawClassRequests]);

  // WAVE CHANGE → build one grid row per Class Roster in that wave, then
  // fetch each roster's current config so the editable columns aren't blank. 
  useEffect(() => {
    if (!selectedWave) {
      setRows([]);
      if (setRosterManagementLength) setRosterManagementLength(0);
      return;
    }

    const baseRows = [];
    rawClassRequests
      .filter((cr) => cr.wave === selectedWave)
      .forEach((cr) => {
        (cr.waveRosterJSON || []).forEach((w) => {
          baseRows.push({
            classRequestId: cr.classRequestId,
            classRosterId: w.classRosterId,
            project: cr.projectName || "",
            site: cr.siteName || "",
            wave: cr.wave,
            isRosterClone: false,
            // Editable fields — populated below once roster config loads
            techEquipmentSolutionId: null,
            distributionTypeId: null,
            ogOracleId: null,
            ogName: "",
            ogEmail: "",
            fdoOracleId: null,
            fdoName: "",
            fdoEmail: "",
            trainerOracleId: null,
            trainerName: "",
            trainerEmail: "",
            configLoaded: false,
          });
        });
      });

    setRows(baseRows);
    if (setRosterManagementLength) setRosterManagementLength(baseRows.length);

    if (baseRows.length === 0) return undefined;

    let cancelled = false;
    setRowsLoading(true);

    Promise.all(
      baseRows.map((row) =>
        getRosterConfigByRosterId(row.classRosterId, false).then((res) => {        
          const item =
            res?.status === true
              ? Array.isArray(res.data)
                ? res.data[0]
                : res.data
              : null;
          return { classRosterId: row.classRosterId, data: item };
        }),
      ),
    )
      .then((results) => {
        if (cancelled) return;
        setRows((prev) =>
          prev.map((row) => {
            const match = results.find(
              (r) => r.classRosterId === row.classRosterId,
            );
            const data = match?.data;
            if (!data) return { ...row, configLoaded: true };
            return {
              ...row,              
              isRosterClone: data.IsRosterClone ?? false,
              techEquipmentSolutionId: data.TechEquipmentSolutionId ?? null,
              distributionTypeId: data.DistributionTypeId ?? null,
              ogOracleId: data.OGOracleId ?? null,
              ogName: data.OGName ?? "",
              ogEmail: data.OGEmail ?? "",
              fdoOracleId: data.FDOOracleId ?? null,
              fdoName: data.FDOName ?? "",
              fdoEmail: data.FDOEmail ?? "",
              trainerOracleId: data.TrainerOracleId ?? null,
              trainerName: data.TrainerName ?? "",
              trainerEmail: data.TrainerEmail ?? "",
              configLoaded: true,
            };
          }),
        );
      })
      .catch(() => {
        if (!cancelled) {
          showError(
            t(
              "alert.error_fetching_roster_config",
              "Some rosters' details could not be loaded.",
            ),
          );
        }
      })
      .finally(() => {
        if (!cancelled) setRowsLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWave, rawClassRequests]);


  // INLINE EDIT HANDLERS
  // Every committed edit to one of the five editable fields flows through
  // updateRow, which updates local grid state AND calls the new
  // updateRosterManagementFields endpoint with that row's 
  const lastUpdatedRowRef = useRef(null);

  // Local-only update: just patches row state, no save call.
  const updateRowLocal = useCallback((classRosterId, patch) => {
    setRows((prev) =>
      prev.map((row) =>
        row.classRosterId === classRosterId ? { ...row, ...patch } : row,
      ),
    );
  }, []);

  const updateRow = useCallback(async (classRosterId, patch) => {
    setRows((prev) => {
      const next = prev.map((row) =>
        row.classRosterId === classRosterId
          ? { ...row, ...patch, isDirty: true }
          : row,
      );
      lastUpdatedRowRef.current =
        next.find((row) => row.classRosterId === classRosterId) || null;
      return next;
    });
    setLoading(true);
    try {
      const updatedRow = lastUpdatedRowRef.current;
      if (updatedRow) {
        const payload = createData(updatedRow);
        const response = await saveRosterManagementFields(payload);        
        if (response?.message === "Success") {
          setSnack({
            open: true,
            message: "Change have been saved Successfully.",
            severity: "success",
          });
        }
      }
    } catch {
      setSnack({
        open: true,
        message: "Error while saving data.",
        severity: "error",
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [t]);

  const handleDistributionChange = (classRosterId, value) => {
    updateRow(classRosterId, { distributionTypeId: value });
  };

  //OG / FDO / Trainer search — handleOGSearch / handleFDOSearch /
  //handleTrainerSearch, generalized across the three fields and scoped to
  //whichever row is currently being edited. 
  const handlePersonSearch = async (field, classRosterId, query) => {
    const trimmedQuery = query?.trim();
    if (!trimmedQuery || trimmedQuery.length < 3) {
      showError(
        t(
          "handleOGTrainerSearchER01",
          "Enter at least 3 characters to search.",
        ),
      );
      return;
    }
    if (!isValidPersonSearch(trimmedQuery)) {
      showError(
        t(
          "handleOGTrainerSearchER03",
          "Enter a valid name, email, or 7-digit Oracle Id.",
        ),
      );
      return;
    }

    setLoading(true);
    try {
      const result = await searchEmployeeProfile(trimmedQuery);
      if (result?.status === true) {
        const mapped = /^\d{7}$/.test(trimmedQuery)
          ? result.data.map((emp) => ({
              OracleId: emp.OracleId,
              Display:
                (emp.Name ?? "") + (emp.OracleId ? ` (${emp.OracleId})` : ""),
              Name: emp.Name,
              Email: emp.Email,
            }))
          : result.data.map((emp) => ({
              OracleId: emp.OracleId,
              Display: emp.Display,
              Name: emp.Name,
              Email: emp.Email,
            }));
        setSearchResults(mapped);
        setActiveSearchField(field);
        setActiveSearchRowId(classRosterId);
        setIsAutocompleteOpen(true);
      } else {
        showError(t("handleOGTrainerSearchER02", "No matching people found."));
      }
    } catch (error) {
      showError(t("handleOGTrainerSearchER02", "No matching people found."));
    } finally {
      setLoading(false);
    }
  };

  const handlePersonSelect = (field, classRosterId, value) => {
    const fieldSet = PERSON_FIELD_MAP[field];
    if (value) {
      updateRow(classRosterId, {
        [fieldSet.nameField]: value.Name,
        [fieldSet.emailField]: value.Email,
        [fieldSet.idField]: value.OracleId,
      });
    } else {     
      updateRow(classRosterId, {
        [fieldSet.nameField]: "",
        [fieldSet.emailField]: "",
        [fieldSet.idField]: null,
      });
    }
    setSearchResults([]);
    setIsAutocompleteOpen(false);
  };

  // Typing in the box (kept separate from the Autocomplete's onInputChange,
  // this updates the visible text,onInputChange below only handles the "cleared the field" side effect).
  const handlePersonNameTyped = (field, classRosterId, newValue) => {
    updateRowLocal(classRosterId, {
      [PERSON_FIELD_MAP[field].nameField]: newValue,
    });
  };

  const handlePersonInputChange = (field, classRosterId, newInputValue, reason) => {
    if (newInputValue === "") {
      const fieldSet = PERSON_FIELD_MAP[field];
      const clearedPatch = {
        [fieldSet.idField]: null,
        [fieldSet.nameField]: "",
        [fieldSet.emailField]: "",
      };
      if (reason === "clear") {        
        updateRow(classRosterId, clearedPatch);
      } else {        
        updateRowLocal(classRosterId, clearedPatch);
      }
      setSearchResults([]);
    }
  };

  // RENDER
  return (
    <>
      {(loading || rowsLoading) && <BackdropComponent />}

      <Grid container spacing={1}>
        <Grid item xs={2}>
          <SubHeading>
            {t("subheading.parent_client", "Parent Client")}
          </SubHeading>
          <Autocomplete
            disablePortal
            blurOnSelect
            options={parentClientDetail}
            getOptionLabel={(option) => option.ParentClientName}
            value={selectedParentClient}
            onChange={(event, newValue) => handleParentClientChange(newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={t(
                  "placeholder.parent_client",
                  "Select parent client…",
                )}
                variant="outlined"
                size="small"
              />
            )}
          />
        </Grid>

        <Grid item xs={1}>
          <SubHeading>{t("subheading.wave_number", "Wave Number")}</SubHeading>
          <Autocomplete
            disablePortal
            blurOnSelect
            disabled={!selectedParentClient || waveOptions.length === 0}
            options={waveOptions}
            getOptionLabel={(option) => String(option)}
            value={selectedWave}
            onChange={(event, newValue) => setSelectedWave(newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={t("placeholder.wave_number", "Select wave…")}
                variant="outlined"
                size="small"
              />
            )}
          />
        </Grid>

        <Grid
          item
          xs={7}
          sx={{
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "center",
            gap: 1,
          }}
        >
          <CustomTooltip title={t("ogTitle", "On-boarding guides")} arrow>
            <Chip label="OG" sx={classes.chipOG} />
          </CustomTooltip>
          <CustomTooltip title={t("ldTitle", "Learning & Development")} arrow>
            <Chip label="L&D" sx={classes.chipLD} />
          </CustomTooltip>
          <CustomTooltip title={t("taTitle", "Talent Acquisition")} arrow>
            <Chip label="TA" sx={classes.chipTA} />
          </CustomTooltip>
          <Alert
            severity="warning"
            sx={{
              width: "fit-content",
              whiteSpace: "nowrap",
              py: 0.25,
              alignItems: "center",
            }}
          >
            {t(
              "alert.ownership_change_warning",
              "Changes here apply to all trainees assigned to this roster.",
            )}
          </Alert>
        </Grid>

        <Grid
          item
          xs={2}
          sx={{
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "flex-end",
          }}
        >
          {rows.length > 0 && (
            <Chip
              label={`${rows.length} ${t("chip.roster_count", "rosters")}`}
              sx={{
                backgroundColor: "#3299FF",
                color: "white",
                fontWeight: "bold",
              }}
            />
          )}
        </Grid>

        <Grid item xs={12}>
          {rows.length > 0 ? (
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{
                maxHeight: "calc(100vh - 340px)",
                overflowY: "auto",
                overflowX: "hidden",
              }}
            >
              <Table
                stickyHeader
                size="small"
                sx={{
                  width: "auto",
                  "& th, & td": { paddingLeft: 2, paddingRight: 2 },
                  "& td": { verticalAlign: "top" },
                }}
              >
                <TableHead>
                  <TableRow
                    sx={{
                      "& th": {
                        fontWeight: "bold",
                        backgroundColor: "#fff",
                        position: "sticky",
                        top: 0,
                        zIndex: 2,
                      },
                    }}
                  >
                    <TableCell>
                      {t("column.class_request_id", "Request Id")}
                    </TableCell>
                    <TableCell>
                      {t("column.class_roster_id", "Roster Id")}
                    </TableCell>
                    <TableCell>{t("column.project", "Project")}</TableCell>
                    <TableCell>{t("column.site", "Site Name")}</TableCell>
                    <TableCell>
                      {t(
                        "column.tech_equipment_solution",
                        "Tech Equipment Solution",
                      )}
                    </TableCell>
                    <TableCell sx={{ color: pillThemeConfig.TA.primaryColor }}>
                      {t("column.distribution_type", "Distribution Type")}
                    </TableCell>
                    <TableCell sx={{ color: pillThemeConfig.OG.primaryColor }}>
                      {t("column.og_name", "OG Name")}
                    </TableCell>
                    <TableCell sx={{ color: pillThemeConfig.OG.primaryColor }}>
                      {t("column.fdo_name", "FDO Name")}
                    </TableCell>
                    <TableCell sx={{ color: pillThemeConfig.LD.primaryColor }}>
                      {t("column.trainer_name", "Trainer Name")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.classRosterId} hover>
                      <TableCell>
                        <Typography
                          variant="body2"
                          onClick={() => {
                            const url = `/home/searchrosters?classRequestId=${row.classRequestId}`;
                            window.open(url, "_blank");
                          }}
                          sx={{
                            color: "#3299FF",
                            fontWeight: 600,
                            cursor: "pointer",
                            "&:hover": { textDecoration: "underline" },
                          }}
                        >
                          {row.classRequestId}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          onClick={() => {
                            const url = `/home/rosterconfig?type=edit&classRosterId=${row.classRosterId}&classRequestId=${row.classRequestId}&data=false`;
                            window.open(url, "_blank");
                          }}
                          sx={{
                            color: "#3299FF",
                            fontWeight: 600,
                            cursor: "pointer",
                            "&:hover": { textDecoration: "underline" },
                          }}
                        >
                          {row.classRosterId}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ minWidth: 120 }}>
                        <ProjectCell
                          t={t}
                          project={row.project}
                          isExpanded={
                            expandedProjectRowId === row.classRosterId
                          }
                          onExpand={() =>
                            setExpandedProjectRowId(row.classRosterId)
                          }
                          onCollapse={() => setExpandedProjectRowId(null)}
                        />
                      </TableCell>
                      <TableCell sx={{ minWidth: 110 }}>{row.site}</TableCell>
                      <TableCell sx={{ minWidth: 110 }}>
                        <Typography variant="body2" sx={ellipsisSx}>
                          {techEquipOptions.find(
                            (opt) =>
                              opt.TechEquipmentSolutionId ===
                              row.techEquipmentSolutionId,
                          )?.TechEquipmentSolution || t("select.none", "—")}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ minWidth: 90 }}>
                        {isEditingCell(row.classRosterId, "dist") ? (
                          <Select
                            open
                            autoFocus
                            fullWidth
                            size="small"
                            displayEmpty
                            value={row.distributionTypeId ?? ""}
                            sx={{ "& .MuiSelect-select": ellipsisSx }}
                            onChange={(e) => {
                              handleDistributionChange(
                                row.classRosterId,
                                e.target.value,
                              );
                              stopEdit();
                            }}
                            onClose={stopEdit}
                          >
                            <MenuItem value="">
                              <em>{t("select.none", "—")}</em>
                            </MenuItem>
                            {distributionOptions.map((opt) => (
                              <MenuItem
                                key={opt.DistributionTypeId}
                                value={opt.DistributionTypeId}
                              >
                                {opt.DistributionType}
                              </MenuItem>
                            ))}
                          </Select>
                        ) : (
                          <Typography
                            variant="body2"
                            onDoubleClick={() =>
                              startEdit(row.classRosterId, "dist")
                            }
                            sx={{ ...ellipsisSx, cursor: "pointer" }}
                          >
                            {distributionOptions.find(
                              (opt) =>
                                opt.DistributionTypeId ===
                                row.distributionTypeId,
                            )?.DistributionType || t("select.none", "—")}
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell sx={{ minWidth: 200 }}>
                        <PersonSearchCell
                          t={t}
                          placeholder={t("inputOGName", "Search OG")}
                          nameValue={row.ogName}
                          emailValue={row.ogEmail}
                          isEditing={isEditingCell(row.classRosterId, "og")}
                          onStartEdit={() => startEdit(row.classRosterId, "og")}
                          onStopEdit={stopEdit}
                          isOpen={
                            isAutocompleteOpen &&
                            activeSearchField === "og" &&
                            activeSearchRowId === row.classRosterId
                          }
                          searchResults={
                            activeSearchField === "og" &&
                            activeSearchRowId === row.classRosterId
                              ? searchResults
                              : []
                          }
                          onOpen={() => setIsAutocompleteOpen(true)}
                          onClose={() => setIsAutocompleteOpen(false)}
                          onInputChange={(event, newInputValue, reason) =>
                            handlePersonInputChange(
                              "og",
                              row.classRosterId,
                              newInputValue,
                              reason,
                            )
                          }
                          onNameTyped={(value) =>
                            handlePersonNameTyped(
                              "og",
                              row.classRosterId,
                              value,
                            )
                          }
                          onSelect={(event, value) =>
                            handlePersonSelect("og", row.classRosterId, value)
                          }
                          onSearch={(query) =>
                            handlePersonSearch("og", row.classRosterId, query)
                          }
                        />
                      </TableCell>

                      <TableCell sx={{ minWidth: 200 }}>
                        <PersonSearchCell
                          t={t}
                          placeholder={t("inputFDOName", "Search FDO")}
                          nameValue={row.fdoName}
                          emailValue={row.fdoEmail}
                          isEditing={isEditingCell(row.classRosterId, "fdo")}
                          onStartEdit={() =>
                            startEdit(row.classRosterId, "fdo")
                          }
                          onStopEdit={stopEdit}
                          isOpen={
                            isAutocompleteOpen &&
                            activeSearchField === "fdo" &&
                            activeSearchRowId === row.classRosterId
                          }
                          searchResults={
                            activeSearchField === "fdo" &&
                            activeSearchRowId === row.classRosterId
                              ? searchResults
                              : []
                          }
                          onOpen={() => setIsAutocompleteOpen(true)}
                          onClose={() => setIsAutocompleteOpen(false)}
                          onInputChange={(event, newInputValue, reason) =>
                            handlePersonInputChange(
                              "fdo",
                              row.classRosterId,
                              newInputValue,
                              reason,
                            )
                          }
                          onNameTyped={(value) =>
                            handlePersonNameTyped(
                              "fdo",
                              row.classRosterId,
                              value,
                            )
                          }
                          onSelect={(event, value) =>
                            handlePersonSelect("fdo", row.classRosterId, value)
                          }
                          onSearch={(query) =>
                            handlePersonSearch("fdo", row.classRosterId, query)
                          }
                        />
                      </TableCell>

                      <TableCell sx={{ minWidth: 200 }}>
                        <PersonSearchCell
                          t={t}
                          placeholder={t("inputTrainerName", "Search Trainer")}
                          nameValue={row.trainerName}
                          emailValue={row.trainerEmail}
                          isEditing={isEditingCell(
                            row.classRosterId,
                            "trainer",
                          )}
                          onStartEdit={() =>
                            startEdit(row.classRosterId, "trainer")
                          }
                          onStopEdit={stopEdit}
                          isOpen={
                            isAutocompleteOpen &&
                            activeSearchField === "trainer" &&
                            activeSearchRowId === row.classRosterId
                          }
                          searchResults={
                            activeSearchField === "trainer" &&
                            activeSearchRowId === row.classRosterId
                              ? searchResults
                              : []
                          }
                          onOpen={() => setIsAutocompleteOpen(true)}
                          onClose={() => setIsAutocompleteOpen(false)}
                          onInputChange={(event, newInputValue, reason) =>
                            handlePersonInputChange(
                              "trainer",
                              row.classRosterId,
                              newInputValue,
                              reason,
                            )
                          }
                          onNameTyped={(value) =>
                            handlePersonNameTyped(
                              "trainer",
                              row.classRosterId,
                              value,
                            )
                          }
                          onSelect={(event, value) =>
                            handlePersonSelect(
                              "trainer",
                              row.classRosterId,
                              value,
                            )
                          }
                          onSearch={(query) =>
                            handlePersonSearch(
                              "trainer",
                              row.classRosterId,
                              query,
                            )
                          }
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "calc(100vh - 425px)",
                width: "100%",
                textAlign: "center",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: "#3F5765",
                  fontWeight: "bold",
                  letterSpacing: "0.10em",
                  padding: 2,
                }}
              >
                {t("typography.no_rosters_loaded", "No rosters loaded")}
              </Typography>
              <Typography
                variant="subtitle1"
                sx={{ color: "#3F5765", letterSpacing: "0.05em", padding: 1 }}
              >
                {t(
                  "typography.select_client_and_wave",
                  "Select a Parent Client, then a Wave Number, to load rosters.",
                )}
              </Typography>
            </Box>
          )}
        </Grid>
      </Grid>

      <SnackBar snack={snack} snackReset={snackReset} />
    </>
  );
}

RosterManagement.propTypes = {
  setRosterManagementLength: PropTypes.func,
};
